import type { CSSProperties } from "react";
import type { CustomRendererProps } from "streamdown";
import { parseSlotBody } from "./parseSlotBody";
import {
  FADING_RULE,
  SlotError,
  SlotReveal,
  SlotShell,
  SlotSkeleton,
} from "./SlotShell";
import type {
  UiOperation,
  UiOperationKind,
  UiOperationPacket,
} from "./ui-operations-types";
import { FintheonNumericText } from "../../numbers/FintheonNumericText";

const KIND_LABELS: Record<UiOperationKind, string> = {
  source_packet: "Source",
  thesis_card: "Thesis",
  evidence_stack: "Evidence",
  contradiction_table: "Contradictions",
  forecast_draft: "Forecast",
  approval_diff: "Approval",
  action_checklist: "Checklist",
};

const CARD_STYLE: CSSProperties = {
  border: "1px solid rgba(199, 159, 74, 0.14)",
  borderRadius: 8,
  background: "rgba(5, 4, 2, 0.46)",
  padding: "10px",
  minWidth: 0,
};

const LABEL_STYLE: CSSProperties = {
  fontFamily: "var(--font-data, ui-monospace, monospace)",
  fontSize: 9,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(199, 159, 74, 0.72)",
};

const BODY_STYLE: CSSProperties = {
  color: "rgba(240, 234, 214, 0.72)",
  fontSize: 12,
  lineHeight: 1.5,
  overflowWrap: "anywhere",
};

interface OperationRow {
  label: string;
  value: string;
}

export function UiOperationsSlot({ code, isIncomplete }: CustomRendererProps) {
  const parsed = parseSlotBody<UiOperationPacket>(code, isIncomplete);
  if (parsed.status === "pending")
    return <SlotSkeleton label="ui operations" lines={4} />;
  if (parsed.status === "error")
    return <SlotError label="ui operations" reason={parsed.reason} />;
  if (!isPacket(parsed.data))
    return <SlotError label="ui operations" reason="Schema mismatch" />;

  const packet = parsed.data;
  if (packet.operations.length === 0) {
    return (
      <SlotShell label="ui operations" muted>
        <div style={BODY_STYLE}>No operations in packet.</div>
      </SlotShell>
    );
  }

  return (
    <SlotReveal>
      <SlotShell label={`ui operations · ${packet.operations.length}`}>
        {packet.title || packet.summary ? (
          <div style={{ marginBottom: 8 }}>
            {packet.title ? (
              <div
                style={{
                  color: "var(--fintheon-text, #f0ead6)",
                  fontSize: 13,
                  fontWeight: 600,
                  overflowWrap: "anywhere",
                }}
              >
                {packet.title}
              </div>
            ) : null}
            {packet.summary ? (
              <div style={BODY_STYLE}>{packet.summary}</div>
            ) : null}
          </div>
        ) : null}
        <div style={{ display: "grid", gap: 8 }}>
          {packet.operations.map((operation) => (
            <OperationCard key={operation.id} operation={operation} />
          ))}
        </div>
      </SlotShell>
    </SlotReveal>
  );
}

function OperationCard({ operation }: { operation: UiOperation }) {
  const rows = makeRows(operation);
  return (
    <article style={CARD_STYLE}>
      <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
        <span style={LABEL_STYLE}>{KIND_LABELS[operation.kind]}</span>
        <span style={{ ...LABEL_STYLE, color: "rgba(240, 234, 214, 0.34)" }}>
          {operation.id}
        </span>
      </div>
      <div
        style={{
          marginTop: 4,
          color: "var(--fintheon-text, #f0ead6)",
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.35,
          overflowWrap: "anywhere",
        }}
      >
        {operation.title}
      </div>
      {operation.summary ? (
        <div style={{ ...BODY_STYLE, marginTop: 4 }}>{operation.summary}</div>
      ) : null}
      {operation.source ? <SourceLine operation={operation} /> : null}
      {rows.length > 0 ? (
        <>
          <div style={FADING_RULE} />
          <div style={{ display: "grid", gap: 6 }}>
            {rows.map((row, index) => (
              <div key={`${row.label}-${index}`} style={{ minWidth: 0 }}>
                <div style={LABEL_STYLE}>{row.label}</div>
                <div style={BODY_STYLE}>
                  <FintheonNumericText text={row.value} />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </article>
  );
}

function SourceLine({ operation }: { operation: UiOperation }) {
  const source = operation.source;
  if (!source) return null;
  const label = [source.label, source.title, source.author]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      style={{ ...BODY_STYLE, marginTop: 6, color: "rgba(199, 159, 74, 0.78)" }}
    >
      {label}
      {source.url ? (
        <a
          href={source.url}
          target="_blank"
          rel="noreferrer"
          style={{
            color: "inherit",
            display: "block",
            overflowWrap: "anywhere",
          }}
        >
          {source.url}
        </a>
      ) : null}
    </div>
  );
}

function makeRows(operation: UiOperation): OperationRow[] {
  const payload = operation.payload;
  if (operation.kind === "source_packet")
    return rowsFromKeys(payload, [
      ["Why", "whyItMatters"],
      ["Evidence", "evidenceCaptured"],
      ["Confirm", "confirmationsToSeek"],
      ["Watch", "contradictionsToWatch"],
      ["Next", "nextAction"],
    ]);
  if (operation.kind === "thesis_card")
    return rowsFromKeys(payload, [
      ["Thesis", "thesis"],
      ["Horizon", "horizon"],
      ["Symbols", "watchedSymbols"],
      ["Confidence", "confidence"],
    ]);
  if (operation.kind === "evidence_stack")
    return rowsFromObjects(payload.rows, "Evidence", [
      "label",
      "evidence",
      "source",
    ]);
  if (operation.kind === "contradiction_table")
    return rowsFromObjects(payload.rows, "Risk", ["risk", "evidence"]);
  if (operation.kind === "forecast_draft")
    return rowsFromKeys(payload, [
      ["Direction", "direction"],
      ["Probability", "probability"],
      ["Timeframe", "timeframe"],
      ["Validate", "validationRule"],
    ]);
  if (operation.kind === "approval_diff")
    return rowsFromKeys(payload, [
      ["Artifact", "artifact"],
      ["Before", "before"],
      ["After", "after"],
    ]);
  if (operation.kind === "action_checklist")
    return rowsFromObjects(payload.items, "Item", ["status", "text"]);
  return rowsFromEntries(payload);
}

function rowsFromKeys(
  payload: Record<string, unknown>,
  keys: [string, string][],
): OperationRow[] {
  return keys
    .map(([label, key]) => ({ label, value: stringifyValue(payload[key]) }))
    .filter((row) => row.value.length > 0);
}

function rowsFromObjects(
  value: unknown,
  fallbackLabel: string,
  keys: string[],
): OperationRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(isRecord)
    .map((item, index) => ({
      label: String(item.label ?? item.risk ?? `${fallbackLabel} ${index + 1}`),
      value: keys
        .filter((key) => key !== "label" && key !== "risk")
        .map((key) => stringifyValue(item[key]))
        .filter(Boolean)
        .join(" · "),
    }))
    .filter((row) => row.value.length > 0);
}

function rowsFromEntries(payload: Record<string, unknown>): OperationRow[] {
  return Object.entries(payload)
    .slice(0, 6)
    .map(([label, value]) => ({ label, value: stringifyValue(value) }))
    .filter((row) => row.value.length > 0);
}

function stringifyValue(value: unknown): string {
  if (typeof value === "number" && value >= 0 && value <= 1)
    return `${Math.round(value * 100)}%`;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value))
    return value.map(stringifyValue).filter(Boolean).join(" · ");
  if (isRecord(value))
    return Object.values(value).map(stringifyValue).filter(Boolean).join(" · ");
  return "";
}

function isPacket(value: unknown): value is UiOperationPacket {
  if (!isRecord(value)) return false;
  if (value.version !== 1 || !Array.isArray(value.operations)) return false;
  return value.operations.every(isOperation);
}

function isOperation(value: unknown): value is UiOperation {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === "string" &&
    typeof value.kind === "string" &&
    value.kind in KIND_LABELS &&
    typeof value.title === "string" &&
    isRecord(value.payload)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
